import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, collection, addDoc, serverTimestamp, query, where, limit, getDocs } from 'firebase/firestore';
import { Star, Send, CheckCircle2, Sparkles, Copy, Wand2, RefreshCw, Loader2 } from 'lucide-react';

const RateReview = () => {
    const { clientId } = useParams();
    const [businessName, setBusinessName] = useState("Loading...");
    const [gmbLink, setGmbLink] = useState("");
    
    const [rating, setRating] = useState(5); 
    const [feedback, setFeedback] = useState(""); 
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    // 🔥 SEO AI STATES
    const [serviceReceived, setServiceReceived] = useState(""); 
    const [selectedTraits, setSelectedTraits] = useState([]); 
    const [aiGeneratedReview, setAiGeneratedReview] = useState(""); 
    const [isGenerating, setIsGenerating] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    
    // 🔥 DYNAMIC UI & SEO STATES
    const [servicePlaceholder, setServicePlaceholder] = useState("e.g., General Service, Consultation...");
    const [targetSeoKeywords, setTargetSeoKeywords] = useState([]); 
    const [traitsList, setTraitsList] = useState([
        "Friendly Staff 🤝", "Quick Service ⚡", "Great Quality 🌟", "Affordable 💰", "Professionalism 👔"
    ]);

    // 🎲 REGENERATE ENGINE STATE
    const [regenCount, setRegenCount] = useState(0);

  useEffect(() => {
        const fetchBusiness = async () => {
            try {
                let data = null;
                // 🔥 SMART ID CLEANER: URL se 'client_' hata kar raw phone number nikalo
                const cleanPhoneId = clientId.replace("client_", ""); 

                // 1. Try to find in "clients" collection with exact ID (client_9198...)
                let docSnap = await getDoc(doc(db, "clients", clientId));
                
                if (docSnap.exists()) {
                    data = docSnap.data();
                } else {
                    // 2. Try to find in "clients" collection with raw phone number
                    docSnap = await getDoc(doc(db, "clients", cleanPhoneId));
                    if (docSnap.exists()) {
                        data = docSnap.data();
                    } else {
                        // 3. Fallback: Check in "users" collection (where most leads are)
                        const userSnap = await getDoc(doc(db, "users", cleanPhoneId));
                        if (userSnap.exists()) {
                            data = userSnap.data();
                        }
                    }
                }

                if (data) {
                    // 🔥 FETCH EXACT NAME
                    const bName = data.name || data.businessCandidate?.name || "Our Business";
                    setBusinessName(bName);
                    
                    let link = data.gmbReviewLink || data.googleReviewLink || "";
                    if (!link || link.includes("pivot-rank.web.app")) {
                        const searchName = encodeURIComponent(bName + " " + (data.city || ""));
                        link = `https://www.google.com/search?q=${searchName}`;
                    }
                    setGmbLink(link);

                    // ===============================================
                    // 🚀 1. THE UNIVERSAL SEO KEYWORD ENGINE
                    // ===============================================
                    let keywords = data.seoKeywords || data.targetKeywords || data.keywords || [];
                    
                    if (keywords.length === 0) {
                        const rawCategory = data.businessCandidate?.category || data.category || "Business";
                        const cityStr = data.city ? ` in ${data.city}` : "";
                        
                        let dbServices = data.courses || data.services || data.products || [];

                        if (dbServices.length > 0) {
                            keywords = [
                                `Best ${rawCategory}${cityStr}`, 
                                `Top ${dbServices[0]}${cityStr}`, 
                                dbServices[1] ? `Best ${dbServices[1]} services` : `Highly rated ${rawCategory}`
                            ];
                        } else {
                            keywords = [
                                `Best ${rawCategory}${cityStr}`, 
                                `Top rated ${rawCategory}${cityStr}`, 
                                `Professional ${rawCategory} near me`
                            ];
                        }
                    }
                    setTargetSeoKeywords(keywords);

                    // ===============================================
                    // 🚀 2. DYNAMIC PLACEHOLDER & TRAITS
                    // ===============================================
                    let dbServices = data.courses || data.services || data.products || [];
                    
                    if (dbServices.length > 0) {
                        setServicePlaceholder(`e.g., ${dbServices[0]}, ${dbServices[1] || 'Service'}...`);
                        
                        if (data.reviewTraits && data.reviewTraits.length > 0) {
                            setTraitsList(data.reviewTraits);
                        } else {
                            setTraitsList(["Great Experience 🌟", "Expert Knowledge 🧠", "Highly Recommended ⭐", "Worth the Money 💰", "Excellent Support 🤝"]);
                        }
                    } else {
                        const cat = (data.businessCandidate?.category || data.category || data.aiKnowledge || "").toLowerCase();
                        
                        if (cat.includes("education") || cat.includes("institute") || cat.includes("course") || cat.includes("class") || cat.includes("academy")) {
                            setTraitsList(["Great Teachers 👨‍🏫", "Good Placements 🎯", "Doubt Clearing 💡", "Practical Learning 💻", "Nice Infrastructure 🏢", "Career Guidance 🚀"]);
                            setServicePlaceholder("e.g., Python, Data Analytics, Digital Marketing...");
                        } 
                        else if (cat.includes("salon") || cat.includes("spa") || cat.includes("hair") || cat.includes("beauty")) {
                            setTraitsList(["Very Clean 🧹", "Relaxing 💆‍♀️", "Trendy Styles ✂️", "Polite Staff 🤝", "Premium Products ✨", "Expert Stylists 💇‍♀️"]);
                            setServicePlaceholder("e.g., Haircut, Bridal Makeup, Facial, Spa...");
                        } 
                        else if (cat.includes("clinic") || cat.includes("dental") || cat.includes("doctor") || cat.includes("hospital")) {
                            setTraitsList(["Painless Treatment 😌", "Very Clean 🏥", "Experienced Doctor 👨‍⚕️", "No Waiting Time ⏳", "Friendly Staff 🤝", "Clear Explanation 📋"]);
                            setServicePlaceholder("e.g., Root Canal, General Checkup, Consultation...");
                        }
                        else if (cat.includes("restaurant") || cat.includes("cafe") || cat.includes("food")) {
                            setTraitsList(["Delicious Food 🍔", "Great Ambience 🍷", "Fast Service 🏃", "Friendly Waiter 🤵", "Value for Money 💸", "Fresh Ingredients 🥗"]);
                            setServicePlaceholder("e.g., Dine-in, Birthday Party, Coffee...");
                        } else {
                            if (data.reviewTraits && data.reviewTraits.length > 0) setTraitsList(data.reviewTraits);
                            setServicePlaceholder("e.g., General Consultation, Support, Purchase...");
                        }
                    }
                } else {
                    setBusinessName("Business Not Found");
                }
            } catch (error) {
                console.error("Error fetching business:", error);
            }
            setIsLoading(false);
        };
        fetchBusiness();
    }, [clientId]);

    const handleStarClick = (selectedStar) => setRating(selectedStar);

    const handleSubmitNegative = async () => {
        if (!feedback.trim()) return;
        setIsLoading(true);
        try {
            await addDoc(collection(db, "clients", clientId, "negative_feedback"), {
                rating: rating,
                message: feedback,
                timestamp: serverTimestamp()
            });
            setIsSubmitted(true);
        } catch (error) {
            console.error("Error saving feedback", error);
        }
        setIsLoading(false);
    };

    const toggleTrait = (trait) => {
        if (selectedTraits.includes(trait)) {
            setSelectedTraits(selectedTraits.filter(t => t !== trait));
        } else {
            setSelectedTraits([...selectedTraits, trait]);
        }
    };

    // 🔥 THE TRUE REGENERATE ENGINE (ULTRA STRICT ENGLISH ONLY) 🔥
    const generateReviewWithAI = async () => {
        if (!serviceReceived.trim() && selectedTraits.length === 0) {
            alert("Please type a service or select at least one thing you liked.");
            return;
        }
        
        setIsGenerating(true);

        // 🔥 STRICT NEGATIVE COMMANDS ADDED HERE
        const reviewStyles = [
            "STRICTLY 100% ENGLISH ONLY. DO NOT USE HINDI OR HINGLISH WORDS. Write a highly positive, natural Google review. Organically include the SEO keywords. Keep it casual. Use 1 emoji.",
            "STRICTLY 100% ENGLISH ONLY. ABSOLUTELY NO HINDI. Write a highly professional and polished Google review. Focus on the quality of service and naturally weave in the target SEO keywords. Use 1-2 emojis.",
            "STRICTLY 100% ENGLISH ONLY. Write an enthusiastic, 2-3 sentence Google review. Ensure the SEO keywords flow naturally without sounding robotic. No Hindi words allowed.",
            "STRICTLY 100% ENGLISH ONLY. NO LOCAL SLANG. Write a grateful personal story style Google review. Mention how the service helped and organically insert the SEO keywords.",
            "STRICTLY 100% ENGLISH ONLY. Write a concise, 5-star Google review. Highlight the specific traits selected and seamlessly include the target SEO keywords. Zero Hindi words."
        ];
        const currentStyle = reviewStyles[regenCount % reviewStyles.length];

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); 
        
        try {
            const response = await fetch("https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/generateSEOReview", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    businessName: businessName,
                    service: serviceReceived,
                    traits: selectedTraits,
                    targetKeywords: targetSeoKeywords.length > 0 ? targetSeoKeywords : ["Highly Recommended", "Great Experience", businessName],
                    styleInstruction: currentStyle, 
                    cacheBuster: Date.now() 
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            
            if (data.success && data.reviewText) {
                setAiGeneratedReview(data.reviewText);
            } else {
                throw new Error("Backend returned false success.");
            }
            
        } catch (error) {
            console.error("🔥 API Failed or Timeout:", error.message);
            
            const tStr = selectedTraits.length > 0 ? `Loved the ${selectedTraits.map(t => t.replace(/[^\w\s]/g, '').trim()).join(" and ")}.` : "";
            const sStr = serviceReceived.trim() ? `Got my ${serviceReceived} done here.` : `Visited recently.`;

            // Fallback mein bhi Universal SEO keyword inject kar rahe hain
            const primaryKw = targetSeoKeywords.length > 0 ? targetSeoKeywords[0] : "best service";
            
            const fallbacks = [
                `Highly recommend ${businessName} for ${primaryKw}! ${sStr} ${tStr} Will definitely come back.`,
                `5 stars for ${businessName}! If you are looking for ${primaryKw}, this is the place. ${sStr} ${tStr}`,
                `Really happy with ${businessName}. ${sStr} ${tStr} Truly provides ${primaryKw}!`,
                `Best place in town for ${primaryKw}! ${sStr} ${tStr} Highly satisfied.`
            ];
            
            setAiGeneratedReview(fallbacks[regenCount % fallbacks.length]);

        } finally {
            clearTimeout(timeoutId);
            setIsGenerating(false);
            setRegenCount(prev => prev + 1); 
        }
    };
const handleCopyAndRedirect = async () => {
        setIsLoading(true);
        const finalReviewToCopy = aiGeneratedReview || feedback; 
        
        if (finalReviewToCopy.trim()) {
            try {
                await navigator.clipboard.writeText(finalReviewToCopy);
                setIsCopied(true);
                await addDoc(collection(db, "clients", clientId, "positive_testimonials"), {
                    rating: rating,
                    message: finalReviewToCopy,
                    service: serviceReceived,
                    timestamp: serverTimestamp()
                });
            } catch (err) {
                console.error("Clipboard copy failed", err);
            }
        }

        // 🔥 THE REDIRECT FIX 🔥
        setTimeout(() => {
            if (gmbLink && !gmbLink.includes("/search?q=")) {
                // Agar asli link hai, toh sidha popup khulega!
                window.location.href = gmbLink;
            } else {
                // Agar link nahi hai, toh Google Maps ke search par bhejo (UX better hai)
                const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessName)}`;
                window.location.href = mapsUrl;
            }
        }, 1500);
    };

    if (isLoading) {
        return <div className="h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div></div>;
    }

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
            <div className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden text-center p-8 border border-slate-100">
                
                {!isSubmitted ? (
                    <div className="animate-in fade-in slide-in-from-bottom-4">
                        <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <Star size={40} className="fill-indigo-500 text-indigo-500" />
                        </div>
                        
                        <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">How was your experience?</h2>
                        <p className="text-slate-500 text-sm font-medium mb-6">
                            Please rate your visit at <br/>
                            <strong className="text-slate-800">{businessName}</strong>
                        </p>

                        <div className="flex justify-center gap-2 mb-8">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button 
                                    key={star}
                                    type="button"
                                    onClick={() => handleStarClick(star)}
                                    className="transform transition-all hover:scale-110 active:scale-95 focus:outline-none"
                                >
                                    <Star 
                                        size={48} 
                                        className={`transition-colors duration-300 ${rating >= star ? 'fill-amber-400 text-amber-400 drop-shadow-md' : 'fill-slate-100 text-slate-200 hover:text-amber-200'}`} 
                                    />
                                </button>
                            ))}
                        </div>

                        {rating > 0 && rating <= 3 && (
                            <div className="animate-in fade-in slide-in-from-bottom-4 text-left bg-slate-50 p-5 rounded-2xl border border-slate-200">
                                <p className="text-sm font-bold text-slate-800 mb-1">We're sorry we fell short. 😔</p>
                                <p className="text-xs text-slate-500 mb-4">Please tell us what went wrong so we can fix it.</p>
                                
                                <textarea 
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    placeholder="Tell us about your experience..."
                                    className="w-full p-3 rounded-xl border border-slate-200 text-sm mb-4 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all resize-none h-24"
                                />
                                
                                <button 
                                    type="button"
                                    onClick={handleSubmitNegative}
                                    disabled={isLoading || !feedback.trim()}
                                    className="w-full bg-slate-900 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-slate-800 transition-all disabled:opacity-50"
                                >
                                    {isLoading ? 'Sending...' : <><Send size={16} /> Send to Management</>}
                                </button>
                            </div>
                        )}

                        {rating >= 4 && (
                            <div className="animate-in fade-in slide-in-from-bottom-4 text-left bg-gradient-to-b from-indigo-50 to-white p-6 rounded-2xl border border-indigo-100 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <Sparkles size={20} className="text-indigo-600" />
                                    <p className="text-base font-black text-indigo-900">You're awesome! 🎉</p>
                                </div>
                                <p className="text-xs text-indigo-700 mb-5 font-medium leading-relaxed">
                                    Help us grow! Answer 2 quick questions and our AI will write a perfect review for you.
                                </p>
                                
                                {!aiGeneratedReview ? (
                                    <div className="space-y-5">
                                        
                                        <div>
                                            <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2 block">1. What service did you get?</label>
                                            <input 
                                                type="text"
                                                value={serviceReceived}
                                                onChange={(e) => setServiceReceived(e.target.value)}
                                                placeholder={servicePlaceholder}
                                                className="w-full p-3 rounded-xl border border-indigo-200 bg-white text-sm font-medium outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all shadow-inner"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2 block">2. What did you like? (Select multiple)</label>
                                            <div className="flex flex-wrap gap-2">
                                                {traitsList.map(trait => (
                                                    <button
                                                        type="button"
                                                        key={trait}
                                                        onClick={() => toggleTrait(trait)}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                                                            selectedTraits.includes(trait) 
                                                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' 
                                                            : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
                                                        }`}
                                                    >
                                                        {trait}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        <button 
                                            type="button"
                                            onClick={generateReviewWithAI}
                                            disabled={isGenerating || (!serviceReceived.trim() && selectedTraits.length === 0)}
                                            className="w-full bg-indigo-600 text-white font-black py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-indigo-700 transition-all disabled:opacity-50 mt-4 shadow-lg shadow-indigo-200"
                                        >
                                            {isGenerating ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    Writing Magic Review...
                                                </div>
                                            ) : (
                                                <><Wand2 size={18} /> Generate My Review</>
                                            )}
                                        </button>
                                        
                                        <button 
                                            type="button"
                                            onClick={handleCopyAndRedirect}
                                            className="w-full text-[10px] text-slate-400 font-bold uppercase tracking-widest hover:text-indigo-600 transition-colors mt-2 text-center"
                                        >
                                            Skip & write directly on Google ⏩
                                        </button>
                                    </div>
                                ) : (
                                    <div className="animate-in zoom-in duration-300">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">AI Generated Review</p>
                                            
                                            <div className="flex items-center gap-3">
                                                <button 
                                                    type="button" 
                                                    onClick={generateReviewWithAI} 
                                                    disabled={isGenerating}
                                                    className="text-[10px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                                                >
                                                    {isGenerating ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} 
                                                    {isGenerating ? 'Drafting...' : 'Regenerate'}
                                                </button>
                                                <button type="button" onClick={() => setAiGeneratedReview("")} className="text-[10px] font-bold text-slate-400 hover:text-slate-600">
                                                    Edit Inputs
                                                </button>
                                            </div>
                                        </div>

                                        <textarea 
                                            value={aiGeneratedReview}
                                            onChange={(e) => setAiGeneratedReview(e.target.value)}
                                            disabled={isGenerating}
                                            className={`w-full p-4 rounded-xl border-2 border-indigo-200 bg-white text-sm mb-4 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition-all resize-none h-36 font-medium text-slate-800 shadow-inner ${isGenerating ? 'opacity-50 animate-pulse' : ''}`}
                                        />
                                        
                                        <button 
                                            type="button"
                                            onClick={handleCopyAndRedirect}
                                            disabled={isLoading || isGenerating}
                                            className="w-full bg-emerald-500 text-white font-black py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-emerald-600 transition-all shadow-xl shadow-emerald-200 uppercase tracking-widest text-[10px] disabled:opacity-70"
                                        >
                                            {isCopied ? (
                                                <><CheckCircle2 size={18} /> Copied! Opening Google Maps...</>
                                            ) : (
                                                <><Copy size={18} /> Copy & Post on Google 🚀</>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="py-8 animate-in zoom-in duration-300">
                        <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <CheckCircle2 size={40} />
                        </div>
                        <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">Feedback Received</h2>
                        <p className="text-slate-500 font-medium">Thank you for letting us know. Our management team has been notified and we will improve our services.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RateReview;